import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { PlatformLocation } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgSelectConfig } from '@ng-select/ng-select';
import { DialogService } from '@ngneat/dialog';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';

import { environment } from '@env/environment';
import { I18nService } from '@app/i18n';
import { AppUpdateService, Logger } from '@app/@core/services';
import { ThemeService } from '@app/shell/services/theme.service';

@UntilDestroy()
@Component({
  selector: 'app-root',
  template: '<router-outlet></router-outlet>',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _router = inject(Router);
  private readonly _titleService = inject(Title);
  private readonly _translateService = inject(TranslateService);
  private readonly _i18nService = inject(I18nService);
  private readonly _themeService = inject(ThemeService);
  private readonly _updateService = inject(AppUpdateService);
  private readonly _ngSelectConfig = inject(NgSelectConfig);
  private readonly _dialogService = inject(DialogService);
  private readonly _bottomSheetService = inject(NgxBottomSheetService);
  private readonly _platformLocation = inject(PlatformLocation);

  ngOnInit() {
    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    // Initialize i18nService with the default language and supported languages
    this._i18nService.init(environment.defaultLanguage, environment.supportedLanguages);

    const onNavigationEnd = this._router.events.pipe(filter((event) => event instanceof NavigationEnd));

    merge(this._translateService.onLangChange, onNavigationEnd)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (event) => {
          const titles = this.getTitle(this._router.routerState, this._router.routerState.root);

          if (titles.length === 0) {
            this._titleService.setTitle(this._translateService.instant('Dashboard'));
          } else {
            const translatedTitles = titles.map((titlePart) => this._translateService.instant(titlePart));
            const uniqueTitles = [...new Set(translatedTitles)];
            const allTitlesSame = uniqueTitles.length === 1;
            this._titleService.setTitle(allTitlesSame ? uniqueTitles[0] : uniqueTitles.join(' | '));
          }

          if (event['lang']) {
            // When language changes, reload the page to fetch language-specific content from API
            window.location.reload();
          }
        },
      });

    // Change page title on navigation or language change, based on route data
    // merge(this.translateService.onLangChange, onNavigationEnd)
    //   .pipe(
    //     map(() => {
    //       let route = this.activatedRoute;
    //       while (route.firstChild) {
    //         route = route.firstChild;
    //       }
    //       return route;
    //     }),
    //     filter((route) => route.outlet === 'primary'),
    //     switchMap((route) => route.data),
    //     untilDestroyed(this)
    //   )
    //   .subscribe((event) => {
    //     const title = event['title'];
    //     if (title) {
    //       this.titleService.setTitle(this.translateService.instant(title));
    //     }
    //   });

    // Router event to check if user triggers browser back then close all overlays
    this._router.events.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart && e.navigationTrigger === 'popstate')).subscribe({
      next: () => this._closeOverlays(),
    });

    // Platform location popstate event to check if user triggers browser back then close all overlays
    this._platformLocation.onPopState(() => this._closeOverlays());

    // Setting up global config for tags
    this._ngSelectConfig.notFoundText = this._translateService.instant('No items available');

    // update service
    this._updateService.subscribeForUpdates();
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(evt: PopStateEvent) {
    this._closeOverlays();
  }

  getTitle(state: any, parent: any): any[] {
    const data = [];
    if (parent && parent.snapshot.data && parent.snapshot.data.title) {
      data.push(parent.snapshot.data.title);
    }

    if (state && parent) {
      data.push(...this.getTitle(state, state.firstChild(parent)));
    }
    return data;
  }

  ngOnDestroy() {
    this._i18nService.destroy();
  }

  private _closeOverlays() {
    this._dialogService.closeAll();
    this._bottomSheetService.closeAll();
  }
}
